import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardMedia, List, Typography } from '@mui/material';
import { IAverageRating } from '@lialo/core/lib/models';
import { formatMoney } from '@lialo/common/lib/formatter/money';
import { AverageTourRating } from '../TourDetail/TourRatings/AverageTourRating';
import { STAR_SIZE_SMALL } from '../../../common/StarRating';
import { range } from '@lialo/common/lib/array';
import { CheckMarkListItem } from '@lialo/pwa-ui/lib/BulletList';

const PREFIX = 'TourCheckoutSummary';

const classes = {
  card: `${PREFIX}-card`,
  headline: `${PREFIX}-headline`,
  responsive: `${PREFIX}-responsive`,
  listItem: `${PREFIX}-listItem`,
  img: `${PREFIX}-img`,
  cardContent: `${PREFIX}-cardContent`,
  price: `${PREFIX}-price`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.card}`]: {
    display: 'flex',
    backgroundColor: 'transparent',
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.headline}`]: {
    fontWeight: theme.typography.fontWeightBold,
  },

  [`& .${classes.responsive}`]: {
    maxWidth: 250,
    width: '33%',
  },

  [`& .${classes.img}`]: {
    paddingTop: '75%',
    width: '100%',
    borderRadius: theme.spacing(1),
  },

  [`& .${classes.cardContent}`]: {
    padding: theme.spacing(0, 0, 0, 2),
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  [`& .${classes.price}`]: {
    fontSize: '1rem',
  },
  [`& .${classes.listItem}`]: {
    padding: theme.spacing(0, 1, 0.5, 1),
  },
}));

interface Props {
  imageSrc?: string;
  title: string;
  price: string;
  averageRating: IAverageRating;
}

export const PurchaseCouponSummary = ({ imageSrc, title, price, averageRating }: Props) => {
  const { t } = useTranslation();

  return (
    <StyledCard elevation={0} className={classes.card}>
      {imageSrc && (
        <div className={classes.responsive}>
          <CardMedia className={classes.img} image={imageSrc} />
        </div>
      )}
      <CardContent className={classes.cardContent}>
        <Typography>{t(`tour_detail.purchase_voucher.summary.pre_title`)}</Typography>
        <Typography className={classes.headline} variant={'subtitle1'} component={'h2'}>
          {title}
        </Typography>
        <AverageTourRating
          totalRatings={averageRating.ratingCount}
          value={averageRating.averageRating ?? 0}
          starSize={STAR_SIZE_SMALL}
        />

        <List>
          {range(1, 5).map(i => (
            <CheckMarkListItem
              className={classes.listItem}
              iconFontSize={'small'}
              textVariant={'body2'}
              key={i}
              title={t(`tour_detail.purchase_voucher.summary.checkmark_${i}`)}
            />
          ))}
        </List>

        <Typography className={classes.price} variant={'subtitle2'}>
          {formatMoney(price)}
        </Typography>
      </CardContent>
    </StyledCard>
  );
};
